import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock } from "lucide-react";
import userAPI from "../../api/UserApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function LoginCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await userAPI.login(formData);
      if (res.code === 200) {
        const { email, username, accessToken } = res.data;

        localStorage.setItem(
          "token",
          JSON.stringify({
            email: email,
            username: username,
          }),
        );
        localStorage.setItem("accessToken", accessToken);
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <div className="login-page-funky">
      <Container fluid className="p-0 h-100">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Brand Poster (Visible on Large Screens) */}
          <Col
            lg={6}
            className="d-none d-lg-flex align-items-center justify-content-center custom-login-left-panel"
          >
            <div className="left-panel-content">
              <span className="badge-text mb-3">WELCOME BACK</span>
              <h1 className="left-panel-heading">CRAVING FOR MORE?</h1>
              <p className="left-panel-sub">
                Log in to track your bags, grab exclusive coupons, and unlock
                tasty rewards! ⚡
              </p>
              <div className="funky-floating-sticker">100% SECURE</div>
            </div>
          </Col>

          {/* Right Side: Login Form Box */}
          <Col
            lg={6}
            className="d-flex justify-content-center align-items-center p-4 p-md-5 bg-white"
          >
            <div className="custom-login-formbox">
              <span className="form-badge">SIGN IN</span>
              <h2 className="login-title-funky mt-3 mb-1">
                Hey, Snack Partner!
              </h2>
              <p className="text-muted mb-4">
                Enter your details to manage your cravings.
              </p>

              <Form onSubmit={handleSubmit}>
                {/* EMAIL */}
                <Form.Group className="mb-4" controlId="email">
                  <Form.Group className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="funky-form-label mb-0">
                      Email Address
                    </Form.Label>
                  </Form.Group>
                  <div className="funky-input-wrapper">
                    <Mail size={18} className="input-icon-left" />
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="hello@vibe.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="funky-form-input"
                    />
                  </div>
                  {errors.email && (
                    <small className="text-danger d-block mt-1 fw-bold">
                      {errors.email}
                    </small>
                  )}
                </Form.Group>

                {/* PASSWORD */}
                <Form.Group className="mb-2" controlId="password">
                  <Form.Group className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="funky-form-label mb-0">
                      Password
                    </Form.Label>
                  </Form.Group>
                  <div className="funky-input-wrapper">
                    <Lock size={18} className="input-icon-left" />
                    <Form.Control
                      type="password"
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="funky-form-input"
                    />
                  </div>
                  {errors.password && (
                    <small className="text-danger d-block mt-1 fw-bold">
                      {errors.password}
                    </small>
                  )}
                </Form.Group>

                {/* FORGOT PASSWORD */}
                <div className="d-flex justify-content-end mb-4">
                  <Link
                    to="/users/forgot-password"
                    className="forgot-pass-link"
                  >
                    Forgot Password?
                  </Link>
                </div>

                {/* SUBMIT BUTTON */}
                <button className="login-submit-btn-funky mb-4" type="submit">
                  LET'S GO ⚡
                </button>

                {/* REGISTER REDIRECT */}
                <div className="text-center pt-3 border-top-funky">
                  <p className="mb-0 text-muted small fw-medium">
                    Don't have an account?
                    <Link
                      to="/users/register"
                      className="register-redirect-link ms-2"
                    >
                      Register Now
                    </Link>
                  </p>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default LoginCanvas;
